/**
 * BRICOLO PERMANENT — le compagnon qui dit quoi faire ensuite (ROADMAP 3.5).
 *
 * ══ Pourquoi ce fichier existe ═══════════════════════════════════════════════
 *
 * Bricolo n'existait que dans le tutoriel (`ui/tutorial.ts`) et disparaissait après —
 * exactement au moment où le joueur commence à avoir besoin qu'on lui dise où regarder.
 * Le §1 exige qu'à chaque instant il ait un objectif atteignable sous les yeux ; sans
 * compagnon, cet objectif vivait dans trois écrans différents qu'il faut penser à ouvrir.
 *
 * ══ Ce qu'il ne fait PAS, et c'est ce qui le rend supportable ════════════════
 *
 * - **il ne parle pas deux fois de suite de la même chose** — un conseil répété devient
 *   un bandeau publicitaire, et on cesse de le lire ;
 * - **il se tait pendant une célébration** : féliciter et conseiller en même temps, c'est
 *   voler son moment au joueur (E§1.4) ;
 * - **il se réduit en pastille** d'un tap, et son état de repli survit à la session ;
 * - **il ne calcule rien** : la décision vit dans `core/advisor.ts`, pure et testée. Ici,
 *   on affiche.
 */

import { t } from '@/i18n';
import type { Advice } from '@/core/advisor';
import type { TranslationKey } from '@/i18n';

import type { Component } from './components';
import { icon } from './icons';

export interface CompanionCallbacks {
  /** Le joueur tape le conseil : on l'emmène là où l'action se fait. */
  readonly onAct: (advice: Advice) => void;
}

export interface CompanionHandle extends Component {
  /** Affiche un conseil, ou rien du tout si `null`. */
  show(advice: Advice | null): void;
  /**
   * Efface le compagnon tant que `covered` est vrai — le tiroir est ouvert par-dessus.
   *
   * Mesuré au format téléphone : le tiroir monte jusqu'aux trois quarts de l'écran, et le
   * compagnon recouvrait la première carte de la boutique (`elementFromPoint` rendait
   * `span.companion__text` au centre de la carte). Un conseil qui vole le tap qu'il
   * conseille de faire est pire qu'un conseil absent.
   *
   * Le taire plutôt que le déplacer : il n'existe aucun coin libre à ce moment-là, et un
   * joueur qui a le tiroir ouvert est déjà en train d'agir.
   */
  setCovered(covered: boolean): void;
  /**
   * Fait taire le compagnon pendant `ms`.
   *
   * Appelé pendant les célébrations : un jalon qui tombe et un conseil qui s'affiche en
   * même temps se disputent le regard, et le conseil gagne toujours — il est plus bas et
   * plus verbeux. On lui coupe la parole le temps de la fanfare.
   */
  mute(ms: number): void;
  destroy(): void;
}

/** Clé de stockage du repli — un joueur qui l'a réduit ne le retrouve pas déplié demain. */
const FOLDED_KEY = 'villopia:companion:folded';

function readFolded(): boolean {
  try {
    return localStorage.getItem(FOLDED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeFolded(folded: boolean): void {
  try {
    localStorage.setItem(FOLDED_KEY, folded ? '1' : '0');
  } catch {
    // Navigation privée, stockage plein : le repli reste vrai pour la session, et c'est
    // tout ce qu'on peut promettre. Perdre un réglage de confort ne casse pas une partie.
  }
}

export function createCompanion(callbacks: CompanionCallbacks): CompanionHandle {
  const el = document.createElement('aside');
  el.className = 'companion';
  el.hidden = true;
  el.setAttribute('aria-live', 'polite');

  const avatar = document.createElement('span');
  avatar.className = 'companion__avatar';
  avatar.append(icon('build'));

  const text = document.createElement('span');
  text.className = 'companion__text';

  const act = document.createElement('button');
  act.type = 'button';
  act.className = 'companion__body';
  act.append(avatar, text);

  const fold = document.createElement('button');
  fold.type = 'button';
  fold.className = 'companion__fold';
  fold.append(icon('chevron'));

  el.append(act, fold);

  let folded = readFolded();
  let current: Advice | null = null;
  let mutedUntil = 0;
  let covered = false;

  const paint = (): void => {
    const silent = Date.now() < mutedUntil || covered;
    el.hidden = current === null || silent;
    el.classList.toggle('companion--folded', folded);
    fold.setAttribute('aria-label', t(folded ? 'advisor.unfold' : 'advisor.fold'));

    if (current === null) return;
    /*
      La sorte du conseil EST la clé de traduction — `advisor.quest`, `advisor.era`…
      Une table « sorte → clé » écrite à côté se désynchroniserait au premier conseil
      ajouté, et le compilateur ne dirait rien puisque les deux resteraient valides
      chacune de son côté (règle 15).
    */
    text.textContent = t(`advisor.${current.kind}` as TranslationKey, {
      count: current.value,
    });
  };

  act.addEventListener('click', () => {
    if (current !== null) callbacks.onAct(current);
  });

  fold.addEventListener('click', () => {
    folded = !folded;
    writeFolded(folded);
    paint();
  });

  paint();

  return {
    el,
    setCovered(next) {
      if (covered === next) return;
      covered = next;
      paint();
    },
    show(advice) {
      // Même conseil, même valeur : on ne repeint pas — sinon l'animation d'entrée se
      // rejouerait dix fois par seconde et le texte scintillerait.
      if (current?.kind === advice?.kind && current?.value === advice?.value) {
        if (Date.now() >= mutedUntil && !covered && current !== null) el.hidden = false;
        return;
      }
      current = advice;
      paint();
    },
    mute(ms) {
      mutedUntil = Math.max(mutedUntil, Date.now() + ms);
      el.hidden = true;
    },
    destroy() {
      el.remove();
    },
  };
}
